import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { filter } from 'rxjs/operators';

/** The only identity the app attaches to Sentry events and replays. */
export interface SentrySessionIdentity {
  salonID: number | string | null | undefined;
  stylistID: number | string | null | undefined;
  /**
   * The signed-in staff member's own email, so support can find the session of
   * the person who reported a problem. Staff only: client data never goes to
   * Sentry, and it is dropped during impersonation.
   */
  stylistEmail?: string | null;
  impersonating: boolean;
}

/**
 * Tags Sentry errors, transactions and session replays with record IDs so
 * support can find the session behind a report ("salon 812 had a problem with
 * client 40211").
 *
 * What goes through here: record IDs (salon, stylist, client), each reduced to
 * a plain positive integer or dropped, plus the signed-in stylist's own email
 * on the Sentry user, never during impersonation. Client data never: a client
 * is only ever a clientID. Names and phone numbers never. Replays mask all
 * text except elements opted in with SENTRY_UNMASK_CLASS (static app text only,
 * see sentry-replay-options.ts). Support looks the ID up in the admin tools
 * and searches Sentry for `clientID:<id>`, `salonID:<id>` or `stylistID:<id>`.
 *
 * Tags live on Sentry's isolation scope, so they apply to every event and
 * replay segment sent while they are set.
 */
@Injectable({ providedIn: 'root' })
export class SentryContextService {
  /**
   * Screens currently working on a client, most recent last, each with the
   * route it was on. A stack rather than one value: a booking modal can open a
   * client page on top of itself, and closing the top one must restore the one
   * underneath.
   *
   * The route is what keeps the tag honest. Ionic keeps pages (and the
   * components embedded in them) alive after you navigate away, and embedded
   * components never hear about it, so an owner's clientID only applies while
   * the app is on the route that owner registered on. Coming back restores it.
   */
  private clientScreens: { owner: object; clientID: string; url: string }[] = [];

  constructor(private router: Router) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.applyClientTag());
  }

  /**
   * Called on every login and session restore.
   *
   * The Sentry user is the stylist's ID plus their own email (staff, not client
   * data -- it is how support recognises who reported a problem).
   *
   * Impersonation keeps the existing policy of never identifying the
   * impersonated stylist to analytics: no Sentry user and no stylistID, so
   * support's own clicks are never attributed to the real stylist. The session
   * is still tagged with the salon and `impersonating: true` so support can
   * find it.
   */
  public identify(identity: SentrySessionIdentity): void {
    const salonID = toSentryId(identity.salonID);
    const stylistID = identity.impersonating ? undefined : toSentryId(identity.stylistID);
    const email = identity.impersonating ? undefined : toStaffEmail(identity.stylistEmail);
    Sentry.setUser(stylistID ? (email ? { id: stylistID, email } : { id: stylistID }) : null);
    Sentry.setTags({
      salonID,
      stylistID,
      impersonating: identity.impersonating ? 'true' : 'false',
    });
  }

  /** Called on logout: nothing about the previous session carries over. */
  public clearIdentity(): void {
    this.clientScreens = [];
    Sentry.setUser(null);
    Sentry.setTags({ salonID: undefined, stylistID: undefined, impersonating: undefined, clientID: undefined });
  }

  /**
   * Marks `owner` (a page or component instance) as working on a client. Call
   * again with a new ID if the screen switches client. Pair with clearClient.
   *
   * An owner is registered on the route it is being shown on the first time it
   * calls this, and keeps that route when it calls again, so a later call can
   * never move it onto another screen. To register it afresh (e.g. a cached
   * page coming back into view), clearClient it first; routed pages do that in
   * ionViewWillLeave.
   *
   * Hazard: Ionic keeps a page and its embedded components alive after you
   * navigate away, and their subscriptions and async loads keep running. A
   * call that arrives while the owner is off screen, with no registration to
   * keep, would register it against whatever screen is showing. So an owner
   * must not call this while it is off screen: guard it the way
   * SaleCheckoutPage's `leftScreen` flag and BookingDetailComponent's
   * `hostActive` input do.
   */
  public setClient(owner: object, clientID: number | string | null | undefined): void {
    const id = toSentryId(clientID);
    const prev = this.clientScreens.find((s) => s.owner === owner);
    this.clientScreens = this.clientScreens.filter((s) => s.owner !== owner);
    if (id) {
      this.clientScreens.push({ owner, clientID: id, url: prev?.url ?? this.routeNow() });
    }
    this.applyClientTag();
  }

  /**
   * Call when `owner` leaves the screen or is destroyed. Safe to repeat. After
   * this, the owner's next setClient registers it on the route showing then,
   * so it must come with the owner back on screen (see setClient's hazard).
   */
  public clearClient(owner: object): void {
    this.clientScreens = this.clientScreens.filter((s) => s.owner !== owner);
    this.applyClientTag();
  }

  private applyClientTag(): void {
    const url = this.router.url;
    const onScreen = this.clientScreens.filter((s) => s.url === url);
    Sentry.setTag('clientID', onScreen[onScreen.length - 1]?.clientID);
  }

  /**
   * The route a screen is being shown on. Pages register from ngOnInit or
   * ionViewWillEnter, which run mid-navigation, before `router.url` updates.
   */
  private routeNow(): string {
    const inFlight = this.router.getCurrentNavigation()?.finalUrl;
    return inFlight ? this.router.serializeUrl(inFlight) : this.router.url;
  }
}

/**
 * A record ID as a string of digits, or undefined. Anything else (a name, an
 * email, a phone number, 0 or a negative "new record" placeholder) is dropped,
 * so this service cannot send personal data even if it is handed some. IDs are
 * capped at 9 digits (the largest client ID is 7 digits today) so a bare
 * 10-digit phone number cannot pass as an ID.
 */
export function toSentryId(value: unknown): string | undefined {
  const text = typeof value === 'number' ? String(value) : typeof value === 'string' ? value.trim() : '';
  return /^[1-9]\d{0,8}$/.test(text) ? text : undefined;
}

/** A plausible single email address, trimmed; anything else is dropped. */
export function toStaffEmail(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const text = value.trim();
  return text.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text) ? text : undefined;
}
